"use client";

import { useEffect, useState } from "react";
import { ArrowRight, Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-16 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex items-center px-3 py-2 text-sm font-medium tracking-wide text-foreground hover:bg-transparent focus:bg-transparent">
                <span className="relative">
                  {item.label}
                  <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-8">
        <div className="h-8 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-48 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-44 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-36 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-1 mt-8">
      {menuItems.map((item, i) => <SheetClose asChild key={item.id}>
          <Link data-index={i} to={item.href || "#"} onClick={onItemClick} className="group block py-4 border-b border-border/60 text-foreground" style={{
        animation: `fadeSlideIn 0.4s ease-out ${i * 80}ms both`
      }}>
            <span className="font-serif text-3xl font-medium tracking-tight group-hover:text-primary transition-colors">
              {item.label}
            </span>
          </Link>
        </SheetClose>)}
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const phoneNumber = "+1 (800) 738-2222";
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="256" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${scrolled ? "bg-background/70 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-12 flex items-center justify-between transition-all duration-300 ${scrolled ? "h-[72px]" : "h-[88px]"}`}>
        <div className="flex items-center flex-shrink-0">
          <Link to="/" className="flex items-center" aria-label="HotelRevBaBa Home">
            <WvcLogo className="h-9 w-auto" />
          </Link>
        </div>

        <div className="hidden lg:flex flex-1 justify-center">
          <MenuProvider menu_id="228" className="header-menu">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <Link to="tel:+18007382222" className="hidden xl:inline-flex items-center gap-2 font-mono text-xs tracking-tight text-foreground/80 hover:text-primary transition-colors">
            <Phone className="size-3.5" />
            {phoneNumber}
          </Link>

          <Button asChild className="hidden sm:inline-flex group rounded-lg h-10 px-5 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
            <Link to="/ContactPage">
              <span className="font-medium">Consult Us</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:bg-accent" aria-label="Open menu">
                <Menu className="size-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-6 sm:p-8">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

              <div className="flex items-center justify-between">
                <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center" aria-label="HotelRevBaBa Home">
                  <WvcLogo className="h-8 w-auto" />
                </Link>
              </div>

              <MenuProvider menu_id="228" className="mobile-menu">
                <MobileMenuConsumer onItemClick={() => setMobileOpen(false)} />
              </MenuProvider>

              <div className="mt-auto pt-8 flex flex-col gap-4">
                <Link to="tel:+18007382222" className="inline-flex items-center gap-2 font-mono text-sm text-foreground/80">
                  <Phone className="size-4" />
                  {phoneNumber}
                </Link>
                <SheetClose asChild>
                  <Button asChild className="group rounded-lg h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 w-full justify-center">
                    <Link to="/ContactPage">
                      <span className="font-medium">Consult Us</span>
                      <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}